/**
 * <s-load-all> — Replaces numbered pagination with one "Load all" button.
 *
 * Attributes:
 *   data-grid     — CSS selector of the list whose <li> items are appended (e.g. "#product-grid").
 *   data-next     — URL of the next paginated page (from Liquid `paginate.next.url`).
 *   data-loading  — Translated label shown while loading.
 *
 * Required child markup:
 *   [data-load-all-button] — The button that starts loading.
 *
 * Each fetched page is parsed, its grid items are appended, and the next page URL
 * is read from that page's own <s-load-all>, until no pages remain.
 */
class SLoadAll extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = async (event) => {
    const button = event.target.closest('[data-load-all-button]');
    if (!button || this.hasAttribute('aria-busy')) return;

    const grid = document.querySelector(this.dataset.grid);
    let next = this.dataset.next;
    if (!grid || !next) return;

    this.setAttribute('aria-busy', 'true');
    const label = button.innerHTML;
    if (this.dataset.loading) button.textContent = this.dataset.loading;

    const parser = new DOMParser();
    try {
      while (next) {
        const response = await fetch(next, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = parser.parseFromString(await response.text(), 'text/html');
        const items = doc.querySelectorAll(`${this.dataset.grid} > li`);
        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.append(document.importNode(item, true)));
        grid.append(fragment);
        const nextHost = doc.querySelector('s-load-all');
        next = nextHost ? nextHost.dataset.next : '';
      }
      this.remove();
    } catch (error) {
      this.removeAttribute('aria-busy');
      button.innerHTML = label;
    }
  };
}

if (!customElements.get('s-load-all')) {
  customElements.define('s-load-all', SLoadAll);
}
